import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCurrentEffect } from '../../../lib/use-current-effect';

import { usePermission } from '../../../hooks/permission';
import { FilterPatientDTO } from '../../../dtos/FilterPatientDTO';
import { filterPatients } from '../../../services/patient';
import { User } from '../../../entities/User';

import DashboardTemplate from '../../../templates/Dashboard';
import PageTree from '../../../components/PageTree';
import { ElementPageTree } from '../../../components/PageTree/types';
import Pagination from '../../../components/Pagination';
import Spinner from '../../../components/Spinner';
import CardContentNothingToDisplay from '../../../components/CardContentNothingToDisplay';
import CloudSketchesImage from '../../../assets/images/cloud-sketches.svg';

import Table from './Table';
import Filter from './Filter';

import {
  Button,
  CardContentNothingToDisplayBox,
  SpinnerContainer,
} from './styles';
import MainAreasEnum from '../../../enums/MainAreasEnum';

export type ListType = {
  users: User[];
  totalPages: number;
  totalPatients: number;
};

// O filtro "Em alerta" saiu na 4.9: ele era alimentado pelo ponteiro de aviso do
// paciente, sempre nulo desde a 0.20, entao **nunca casava ninguem** -- era uma opcao
// visivel ao usuario que devolvia lista vazia por construcao. A fila de verdade e a
// tela `/alerts`.
const DEFAULT_FILTERS: FilterPatientDTO = {
  orderBy: 'registration',
  ordenationType: 'DESC',
  status: 'active',
};

function isValidDate(date: Date) {
  return !Number.isNaN(date.getTime());
}

function parsePageFromSearch(search: string) {
  const params = new URLSearchParams(search);
  const pageFromUrl = Number(params.get('page') || 1);

  return Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
}

function parseFiltersFromSearch(search: string): Partial<FilterPatientDTO> & {
  cpf?: string;
  phone?: string;
  icd10?: string;
} {
  const params = new URLSearchParams(search);

  const parsed: Partial<FilterPatientDTO> & {
    cpf?: string;
    phone?: string;
    icd10?: string;
  } = {};

  const maybeName = params.get('name') || '';
  if (maybeName) parsed.name = maybeName;

  const maybeCpf = params.get('cpf') || '';
  if (maybeCpf) parsed.cpf = maybeCpf;

  const maybePhone = params.get('phone') || '';
  if (maybePhone) parsed.phone = maybePhone;

  const maybeRisk = params.get('risk') || '';
  if (maybeRisk) parsed.risk = maybeRisk;

  const maybeSheet = params.get('sheet') || '';
  if (maybeSheet) parsed.sheet = maybeSheet;

  const maybeStatus = params.get('status') as FilterPatientDTO['status'] | null;
  if (
    maybeStatus === 'active' ||
    maybeStatus === 'draft' ||
    maybeStatus === 'inactive'
  ) {
    parsed.status = maybeStatus;
  }

  const maybeOrderBy = params.get('orderBy') || '';
  if (maybeOrderBy) parsed.orderBy = maybeOrderBy;

  const maybeOrdenationType = params.get('ordenationType') as
    | FilterPatientDTO['ordenationType']
    | null;
  if (maybeOrdenationType === 'ASC' || maybeOrdenationType === 'DESC') {
    parsed.ordenationType = maybeOrdenationType;
  }

  const maybeIcd10 = params.get('icd10') || '';
  if (maybeIcd10) parsed.icd10 = maybeIcd10;

  const maybeStartDate = params.get('startDate') || '';
  if (maybeStartDate) {
    const d = new Date(maybeStartDate);
    if (isValidDate(d)) parsed.startDate = d;
  }

  const maybeEndDate = params.get('endDate') || '';
  if (maybeEndDate) {
    const d = new Date(maybeEndDate);
    if (isValidDate(d)) parsed.endDate = d;
  }

  return parsed;
}

function setOrDeleteParam(
  params: URLSearchParams,
  key: string,
  value: string | number | undefined | null,
) {
  if (value === undefined || value === null || String(value).trim() === '') {
    params.delete(key);
    return;
  }

  params.set(key, String(value));
}

const List: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const { hasPermission } = usePermission();

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ListType>({
    users: [],
    totalPages: 0,
    totalPatients: 0,
  });

  const [currentPage, setCurrentPage] = useState(() =>
    parsePageFromSearch(location.search),
  );

  const [filters, setFilters] = useState<FilterPatientDTO>({
    ...DEFAULT_FILTERS,
    ...parseFiltersFromSearch(location.search),
  });

  const canCreatePatient = useMemo(
    () => hasPermission('register_patient'),
    [hasPermission],
  );
  const pages = useMemo<ElementPageTree[]>(() => [{ name: 'Pacientes' }], []);

  useEffect(() => {
    // Mantém o estado sincronizado com a URL (back/forward e links compartilhados)
    const pageFromUrl = parsePageFromSearch(location.search);
    const filtersFromUrl = parseFiltersFromSearch(location.search);

    setCurrentPage(pageFromUrl);
    setFilters((prev) => ({
      ...DEFAULT_FILTERS,
      ...prev,
      ...filtersFromUrl,
    }));
  }, [location.search]);

  const handleChangePage = (page: number) => {
    const params = new URLSearchParams(history.location.search);
    params.set('page', String(page));

    setCurrentPage(page);

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  };

  useEffect(() => {
    // Persiste filtros na URL para permitir "voltar" com estado intacto
    const params = new URLSearchParams(history.location.search);

    setOrDeleteParam(params, 'name', filters.name);
    // @ts-expect-error: esses campos existem no formulário, mas não no DTO tipado
    setOrDeleteParam(params, 'cpf', filters.cpf);
    // @ts-expect-error: esses campos existem no formulário, mas não no DTO tipado
    setOrDeleteParam(params, 'phone', filters.phone);
    setOrDeleteParam(params, 'risk', filters.risk);
    setOrDeleteParam(params, 'sheet', filters.sheet);
    setOrDeleteParam(params, 'status', filters.status);

    // Mantém URL mais limpa (omite defaults)
    setOrDeleteParam(
      params,
      'orderBy',
      filters.orderBy && filters.orderBy !== DEFAULT_FILTERS.orderBy
        ? filters.orderBy
        : '',
    );
    setOrDeleteParam(
      params,
      'ordenationType',
      filters.ordenationType &&
        filters.ordenationType !== DEFAULT_FILTERS.ordenationType
        ? filters.ordenationType
        : '',
    );
    // @ts-expect-error: esse campo existe no formulário, mas não no DTO tipado
    setOrDeleteParam(params, 'icd10', filters.icd10);

    setOrDeleteParam(
      params,
      'startDate',
      filters.startDate && isValidDate(filters.startDate)
        ? filters.startDate.toISOString()
        : '',
    );
    setOrDeleteParam(
      params,
      'endDate',
      filters.endDate && isValidDate(filters.endDate)
        ? filters.endDate.toISOString()
        : '',
    );

    const nextSearch = params.toString();
    const currentSearch = history.location.search.replace(/^\?/, '');

    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: history.location.pathname,
        search: nextSearch,
      });
    }
  }, [filters, history]);

  useCurrentEffect(
    (isCurrent) => {
      filterPatients({
        dataFilter: filters,
        currentPage,
      })
        .then((response) => {
          if (isCurrent()) {
            setList({
              users: response.list,
              totalPages: response.totalPage,
              totalPatients: response.total,
            });
          }
        })
        .finally(() => {
          if (isCurrent()) {
            setLoading(false);
          }
        });
    },
    [currentPage, filters],
  );

  const handleCreatePatient = useCallback(
    (local) => history.push(`/${local}/register`),
    [history],
  );

  return (
    <DashboardTemplate name={MainAreasEnum.PATIENTS}>
      <PageTree pages={pages} />
      <Button
        onClick={() => handleCreatePatient('patient')}
        color="primary"
        disabled={!canCreatePatient}
      >
        Novo Paciente
      </Button>

      <Filter setFilters={setFilters} onChangePage={handleChangePage} />

      {loading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : list.users.length > 0 ? (
        <>
          <Table filters={filters} list={list} setFilters={setFilters} />

          <Pagination
            totalPages={list.totalPages}
            onChangePage={handleChangePage}
            page={currentPage}
          />
        </>
      ) : (
        <CardContentNothingToDisplayBox>
          <CardContentNothingToDisplay>
            <img src={CloudSketchesImage} alt="" />
            Nenhum dado encontrado
          </CardContentNothingToDisplay>
        </CardContentNothingToDisplayBox>
      )}
    </DashboardTemplate>
  );
};

export default List;
